import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest, postRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import ActionModalHeader from "../../Users/modals/ActionModalHeader";
import { affiliateName, clientName, inr } from "../components/AffiliateLayout";

interface AddPayoutModalProps {
    show: boolean;
    onHide: () => void;
    // opened from an affiliate row / drawer: that affiliate is picked and all their commissions ticked
    affiliate?: any;
    onSuccess?: () => void;
}

// Manual payout: pick an affiliate, tick their unpaid commissions (not yet in a weekly settlement).
// The payout is created as Pending; it is marked Paid from the Payouts tab after the bank transfer.
const AddPayoutModal = ({ show, onHide, affiliate, onSuccess }: AddPayoutModalProps) => {
    const [isLoader, setIsLoader] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [commissionLoader, setCommissionLoader] = useState(false);

    const [affiliateUserList, setAffiliateUserList] = useState<any[]>([]);
    const [commissionList, setCommissionList] = useState<any[]>([]);

    const [affiliateId, setAffiliateId] = useState("");
    const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
    const [remark, setRemark] = useState("");

    // Fetch affiliates that have unpaid commission
    const getAffiliateUserList = async () => {
        try {
            setIsLoader(true);
            const response = await getRequest("/affiliate/affiliate-payout-user-list?due=1");

            if (response.status === "success") {
                setAffiliateUserList(response.data || []);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading affiliates!");
        } finally {
            setIsLoader(false);
        }
    };

    // Fetch unpaid commissions of one affiliate
    const getCommissionList = async (id: string, selectAll: boolean) => {
        try {
            setCommissionLoader(true);
            setSelectedCommissions([]);
            const response = await getRequest(`/affiliate/affiliate-payout-commission-list?affiliateId=${id}`);

            if (response.status === "success") {
                setCommissionList(response.data || []);
                if (selectAll) setSelectedCommissions((response.data || []).map((c: any) => c._id));
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading commissions!");
        } finally {
            setCommissionLoader(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        setRemark("");
        setCommissionList([]);
        setSelectedCommissions([]);
        getAffiliateUserList();
        if (affiliate?._id) {
            setAffiliateId(affiliate._id);
            getCommissionList(affiliate._id, true);
        } else {
            setAffiliateId("");
        }
    }, [show, affiliate]);

    const handleAffiliateChange = (id: string) => {
        setAffiliateId(id);
        if (id) {
            getCommissionList(id, true);
        } else {
            setCommissionList([]);
            setSelectedCommissions([]);
        }
    };

    const toggleCommission = (id: string) => {
        setSelectedCommissions(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    };

    const allChecked = commissionList.length > 0 && selectedCommissions.length === commissionList.length;
    const total = commissionList
        .filter(item => selectedCommissions.includes(item._id))
        .reduce((sum, item) => sum + Number(item.ac_amount || 0), 0);

    const handleSubmit = async () => {
        if (!affiliateId) return toast.warning("Please select an affiliate");
        if (selectedCommissions.length === 0) return toast.warning("Please select at least one commission");

        try {
            setIsSaving(true);
            // the amount is worked out on the server from the selected commissions
            const response = await postRequest("/affiliate/add-payout-proccess", {
                affiliateId,
                commissionIds: selectedCommissions,
                remark: remark.trim(),
            });

            if (response.status === "success") {
                toast.success(response.message);
                onSuccess?.();
                onHide();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setIsSaving(false);
        }
    };

    // keep the preselected affiliate in the dropdown even if the list has not loaded it
    const options = affiliate?._id && !affiliateUserList.some(a => a._id === affiliate._id)
        ? [affiliate, ...affiliateUserList]
        : affiliateUserList;

    return (
        <Modal show={show} onHide={onHide} centered size="lg" contentClassName="cx">
            <ActionModalHeader
                icon="ri-bank-line"
                title="Create payout"
                subtitle="Groups unpaid commissions into one payout. Mark it paid from Payouts after the bank transfer."
                onClose={onHide}
            />
            <div style={{ padding: "14px 20px" }}>
                <div className="cx-fgrid">
                    <div>
                        <label className="cx-label">Affiliate</label>
                        <select className="cx-select" value={affiliateId} onChange={(e) => handleAffiliateChange(e.target.value)} disabled={isLoader}>
                            <option value="">{isLoader ? "Loading…" : "Select affiliate"}</option>
                            {options.map(a => (
                                <option key={a._id} value={a._id}>
                                    {affiliateName(a)}{a.affiliate_companyName ? ` · ${a.affiliate_companyName}` : ""}
                                </option>
                            ))}
                        </select>
                        {!isLoader && !affiliateUserList.length && (
                            <p className="cx-help">No affiliate has unpaid commission outside a payout right now.</p>
                        )}
                    </div>
                    <div>
                        <label className="cx-label">Payout amount</label>
                        <div className="cx-input mono" style={{ display: "flex", alignItems: "center", background: "#f7f8fa", fontWeight: 600 }}>
                            {inr(total)}
                        </div>
                        <p className="cx-help">{selectedCommissions.length} of {commissionList.length} commissions selected</p>
                    </div>
                </div>

                <label className="cx-label" style={{ marginTop: 14 }}>Unpaid commissions</label>
                <div className="cx-card" style={{ maxHeight: 260, overflowY: "auto" }}>
                    <table className="cx-tbl">
                        <thead>
                            <tr>
                                <th style={{ width: 20 }}>
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        disabled={!commissionList.length}
                                        onChange={(e) => setSelectedCommissions(e.target.checked ? commissionList.map(c => c._id) : [])}
                                    />
                                </th>
                                <th>Commission</th>
                                <th>Client</th>
                                <th>Date</th>
                                <th className="r">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {commissionLoader ? (
                                <tr><td colSpan={5}><div className="cx-empty" style={{ padding: 20 }}>Loading commissions…</div></td></tr>
                            ) : commissionList.length ? commissionList.map(item => (
                                <tr key={item._id} className="click" onClick={() => toggleCommission(item._id)}>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={selectedCommissions.includes(item._id)} onChange={() => toggleCommission(item._id)} />
                                    </td>
                                    <td><span className="mono">#{item.ac_unique_id || item.ac_id}</span></td>
                                    <td>{clientName(item.ac_user_id)}</td>
                                    <td className="fs12 muted">{moment(item.createdAt).format("DD MMM YYYY")}</td>
                                    <td className="r mono">{inr(item.ac_amount)}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5}><div className="cx-empty" style={{ padding: 20 }}>{affiliateId ? "No unpaid commissions outside a payout." : "Select an affiliate to see their unpaid commissions."}</div></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <label className="cx-label" style={{ marginTop: 14 }}>Remark <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                <textarea className="cx-input" rows={2} placeholder="e.g. September referral payout" value={remark} onChange={(e) => setRemark(e.target.value)}></textarea>
            </div>
            <div style={{ padding: "0 20px 18px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" className="cx-btn cx-btn-light" onClick={onHide}>Cancel</button>
                <button type="button" className="cx-btn cx-btn-primary" onClick={handleSubmit} disabled={isSaving || selectedCommissions.length === 0}>
                    {isSaving ? "Creating…" : `Create payout · ${inr(total)}`}
                </button>
            </div>
        </Modal>
    );
};

export default AddPayoutModal;
